import React, { useState, useEffect, useMemo } from 'react';
import { collection, doc, updateDoc, onSnapshot, addDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../../config/firebaseConfig';
import { getOnlineEmployeeIds, toMillis } from '../../utils/presence';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  FaUserLock,
  FaSearch,
  FaTimes,
  FaSave,
  FaUndo,
  FaCheck,
  FaLock,
  FaLayerGroup,
  FaUsers,
  FaUserShield,
  FaKey,
  FaSpinner,
  FaEnvelope,
  FaCircle,
  FaChartBar,
  FaPaperPlane,
  FaMapMarkerAlt,
  FaUserEdit
} from 'react-icons/fa';
import { menuItems, SPECIAL_EMAIL_ACCESS } from '../../components/SideBar';
import { useTheme } from '../../contexts/ThemeContext';
import UsageStatsModal from './UsageStatsModal';
import styles from './PageAccessManagementPage.module.css';

// The management tool itself must never be grantable through this UI.
const SELF_PATH = '/sayfa-yetkileri';

// Cloud Function endpoints
const SET_PASSWORD_URL =
  'https://us-central1-habcare-8ecad.cloudfunctions.net/adminSetUserPassword';
const CREDENTIALS_EMAIL_URL =
  'https://us-central1-habcare-8ecad.cloudfunctions.net/sendAccountCredentialsEmailV2';

// Güçlü şifre üret (CreateUserModal ile aynı mantık)
const generateStrongPassword = (length = 12) => {
  const charset =
    'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+';
  let pw = '';
  for (let i = 0; i < length; ++i) {
    pw += charset.charAt(Math.floor(Math.random() * charset.length));
  }
  if (!/[A-Z]/.test(pw)) pw += 'A';
  if (!/[a-z]/.test(pw)) pw += 'a';
  if (!/[0-9]/.test(pw)) pw += '1';
  if (!/[!@#$%^&*()_+]/.test(pw)) pw += '!';
  return pw.slice(0, length);
};

// Build a flat, de-duplicated list of navigable pages grouped by their menu.
const buildPages = () => {
  const pages = [];
  const seen = new Set();
  menuItems.forEach((group) => {
    const children =
      group.submenu && group.submenu.length > 0
        ? group.submenu.map((sub) => ({
            group: group.title,
            title: sub.title,
            path: sub.path,
            requiredAuth: sub.requiredAuth || group.requiredAuth || []
          }))
        : [
            {
              group: group.title,
              title: group.title,
              path: group.path,
              requiredAuth: group.requiredAuth || []
            }
          ];
    children.forEach((page) => {
      if (page.path === SELF_PATH) return;
      if (seen.has(page.path)) return;
      seen.add(page.path);
      pages.push(page);
    });
  });
  return pages;
};

// `authorization` may be stored as a single string (single-select dropdown in
// Çalışan Yönetimi) or, for some records, an array. Normalize to a role array.
const getRoles = (emp) => {
  const a = emp && emp.authorization;
  if (Array.isArray(a)) return a.length > 0 ? a : ['employee'];
  if (typeof a === 'string' && a.trim()) return [a.trim()];
  return ['employee'];
};

// Yetkilendirme ("authorization") alanı "admin" olan çalışanları tespit et.
const isAdmin = (emp) =>
  getRoles(emp).some((r) => String(r).trim().toLocaleLowerCase('tr-TR') === 'admin');

// Yetkilendirme ("authorization") alanı "Kısıtlı Admin" olan çalışanları tespit et.
const isRestrictedAdmin = (emp) =>
  getRoles(emp).some((r) => String(r).trim().toLocaleLowerCase('tr-TR') === 'kısıtlı admin');

const isRoleGranted = (page, roles) =>
  !page.requiredAuth || page.requiredAuth.length === 0
    ? true
    : page.requiredAuth.some((r) => roles.includes(r));

const isSpecialGranted = (page, email) => {
  const sp = SPECIAL_EMAIL_ACCESS[(email || '').toLowerCase()];
  return sp ? sp.includes(page.path) : false;
};

const initials = (emp) =>
  `${(emp.eName || '').charAt(0)}${(emp.eSurname || '').charAt(0)}`.toUpperCase() || '?';

// Rota yolunu ("/project-management") menüdeki okunabilir karşılığına çevirir
// ("Proje Menü · Proje Yönetimi"). Menüde olmayan yollar için yolun kendisi
// gösterilir, böylece yeni eklenen sayfalar da bir şey ifade eder.
const buildPathLabels = () => {
  const labels = {};
  menuItems.forEach((group) => {
    if (group.path) {
      labels[group.path] = { group: group.title, title: group.title };
    }
    (group.submenu || []).forEach((sub) => {
      if (sub.path) labels[sub.path] = { group: group.title, title: sub.title };
    });
  });
  // Menüde yer almayan ama sık kullanılan sayfalar
  labels['/profile'] = { group: 'Profil', title: 'Profil' };
  labels['/'] = { group: 'Ana Sayfa', title: 'Ana Sayfa' };
  return labels;
};

const PATH_LABELS = buildPathLabels();

const describePath = (path) => {
  if (!path) return null;
  const known = PATH_LABELS[path];
  if (known) return known;
  // /project-management/123 gibi alt yollar için üst yolu dene
  const base = `/${String(path).split('/').filter(Boolean)[0] || ''}`;
  if (PATH_LABELS[base]) return PATH_LABELS[base];
  return { group: 'Diğer', title: path };
};

// "4 dk", "1 sa 12 dk" gibi kısa süre etiketi
const formatDuration = (ms) => {
  if (!Number.isFinite(ms) || ms < 0) return null;
  const minutes = Math.floor(ms / 60000);
  if (minutes < 1) return 'az önce';
  if (minutes < 60) return `${minutes} dk`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest > 0 ? `${hours} sa ${rest} dk` : `${hours} sa`;
};

const PageAccessManagementPage = () => {
  const { isDarkMode } = useTheme();
  const allPages = useMemo(buildPages, []);

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [pageSearch, setPageSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [draftGrants, setDraftGrants] = useState([]); // working list of granted page paths
  const [saving, setSaving] = useState(false);
  const [showAdminsModal, setShowAdminsModal] = useState(false);
  const [showRestrictedAdminsModal, setShowRestrictedAdminsModal] = useState(false);
  const [showOnlineUsersModal, setShowOnlineUsersModal] = useState(false);
  // Çevrimiçi listesinden mesaj gönderme
  const [messageTarget, setMessageTarget] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  
  // Online status is derived from each employee's `lastSeen` heartbeat further
  // down (see the presence ticker), not stored as its own state.

  // Şifre değiştirme modal state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState('');

  // Yetkilendirmesi "admin" olan tüm çalışanlar
  const adminEmployees = useMemo(
    () => employees.filter(isAdmin),
    [employees]
  );

  // Yetkilendirmesi "Kısıtlı Admin" olan tüm çalışanlar
  const restrictedAdminEmployees = useMemo(
    () => employees.filter(isRestrictedAdmin),
    [employees]
  );

  // Pages grouped by menu section, honoring the page search filter
  const groupedPages = useMemo(() => {
    const term = pageSearch.trim().toLowerCase();
    const groups = [];
    const byTitle = {};
    allPages.forEach((page) => {
      if (
        term &&
        !page.title.toLowerCase().includes(term) &&
        !page.path.toLowerCase().includes(term) &&
        !page.group.toLowerCase().includes(term)
      ) {
        return;
      }
      if (!byTitle[page.group]) {
        byTitle[page.group] = { group: page.group, pages: [] };
        groups.push(byTitle[page.group]);
      }
      byTitle[page.group].pages.push(page);
    });
    return groups;
  }, [allPages, pageSearch]);

  // Çalışan listesi canlı dinlenir. Önceden bu liste 5 saniyede bir
  // getDocs(collection('Employees')) ile baştan çekiliyordu; her çekiş
  // koleksiyondaki HER dokümanı okuduğu için maliyet çalışan sayısıyla
  // çarpılıyordu (5 sn'de bir × N çalışan ≈ günde 17.280 × N okuma).
  // onSnapshot ilk yüklemede bir kez okur, sonrasında yalnızca değişen
  // dokümanları getirir — hem çok daha ucuz hem de anlık.
  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'Employees'),
      (snap) => {
        // Yalnızca aktif çalışanlar (LoginPage ile aynı kontrol) listelenir
        const list = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((e) => Number(e.Active) === 1);
        list.sort((a, b) =>
          `${a.eName || ''} ${a.eSurname || ''}`.localeCompare(
            `${b.eName || ''} ${b.eSurname || ''}`,
            'tr'
          )
        );
        setEmployees(list);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching employees:', error);
        toast.error('Çalışanlar yüklenirken bir hata oluştu');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  // Çevrimiçilik son nabzın yaşına bakar, bu yüzden yeni bir Firestore olayı
  // gelmese bile zamanla eskimeli. Saniye sayacı bunu sağlar.
  const [presenceNow, setPresenceNow] = useState(() => Date.now());
  useEffect(() => {
    const tick = setInterval(() => setPresenceNow(Date.now()), 15000);
    const handleVisibilityChange = () => {
      if (!document.hidden) setPresenceNow(Date.now());
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      clearInterval(tick);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const onlineUsers = useMemo(
    () => getOnlineEmployeeIds(employees, presenceNow),
    [employees, presenceNow]
  );

  // Online olan tüm çalışanlar
  const onlineEmployees = useMemo(
    () => employees.filter((emp) => onlineUsers.has(emp.id)),
    [employees, onlineUsers]
  );

  // Çevrimiçi kullanıcıların o an bulunduğu sayfa ve orada geçirdiği süre
  const onlineActivity = useMemo(() => {
    const map = {};
    onlineEmployees.forEach((emp) => {
      const page = describePath(emp.currentPath);
      const since = toMillis(emp.currentPathAt);
      map[emp.id] = {
        page,
        duration: since ? formatDuration(presenceNow - since) : null,
      };
    });
    return map;
  }, [onlineEmployees, presenceNow]);

  // Mesaj, projedeki mevcut Messenger ile aynı `messages` koleksiyonuna
  // yazılır; alıcı mesajı normal mesaj kutusunda görür ve cevaplayabilir.
  const sendMessage = async () => {
    const text = messageText.trim();
    if (!messageTarget || !text) return;

    const senderEmail = auth.currentUser?.email;
    const receiverEmail = messageTarget.eMail;

    if (!senderEmail) {
      toast.error('Oturum bilgisi bulunamadı, mesaj gönderilemedi.');
      return;
    }
    if (!receiverEmail) {
      toast.error('Bu çalışanın e-posta adresi tanımlı değil, mesaj gönderilemez.');
      return;
    }

    setSendingMessage(true);
    try {
      await addDoc(collection(db, 'messages'), {
        senderEmail,
        receiverEmail,
        text,
        fileUrl: null,
        fileName: null,
        timestamp: serverTimestamp(),
        read: false,
      });
      toast.success(`Mesaj ${messageTarget.eName || ''} ${messageTarget.eSurname || ''} kişisine gönderildi`);
      setMessageTarget(null);
      setMessageText('');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Mesaj gönderilirken bir hata oluştu');
    } finally {
      setSendingMessage(false);
    }
  };

  const selectedEmployee = useMemo(
    () => employees.find((e) => e.id === selectedId) || null,
    [employees, selectedId]
  );

  const selectedRoles = useMemo(
    () => (selectedEmployee ? getRoles(selectedEmployee) : []),
    [selectedEmployee]
  );

  const selectEmployee = (emp) => {
    setSelectedId(emp.id);
    setDraftGrants(Array.isArray(emp.pageAccess) ? [...emp.pageAccess] : []);
    setPageSearch('');
  };

  // Effective access for a page, given an explicit DB grant list
  const pageState = (page, grants, email, roles) => {
    if (isRoleGranted(page, roles)) return 'role';
    if (isSpecialGranted(page, email)) return 'special';
    if (grants.includes(page.path)) return 'granted';
    return 'none';
  };

  const accessibleCount = (emp) => {
    const roles = getRoles(emp);
    const grants = Array.isArray(emp.pageAccess) ? emp.pageAccess : [];
    return allPages.filter(
      (p) => pageState(p, grants, emp.eMail, roles) !== 'none'
    ).length;
  };

  const toggleGrant = (page) => {
    setDraftGrants((prev) =>
      prev.includes(page.path)
        ? prev.filter((p) => p !== page.path)
        : [...prev, page.path]
    );
  };

  // Unsaved-change detection (order-insensitive)
  const originalGrants = useMemo(
    () =>
      selectedEmployee && Array.isArray(selectedEmployee.pageAccess)
        ? selectedEmployee.pageAccess
        : [],
    [selectedEmployee]
  );
  const hasChanges = useMemo(() => {
    const a = [...originalGrants].sort();
    const b = [...draftGrants].sort();
    return a.length !== b.length || a.some((v, i) => v !== b[i]);
  }, [originalGrants, draftGrants]);

  const resetDraft = () => setDraftGrants([...originalGrants]);

  const handleSave = async () => {
    if (!selectedEmployee) return;
    setSaving(true);
    try {
      // Persist only meaningful grants: pages not already covered by role or
      // the hardcoded email allow-list. Preserve any paths outside this tool.
      const knownPaths = new Set(allPages.map((p) => p.path));
      const cleanedGrants = draftGrants.filter((path) => {
        const page = allPages.find((p) => p.path === path);
        if (!page) return false;
        const state = pageState(page, [path], selectedEmployee.eMail, selectedRoles);
        return state === 'granted';
      });
      const preserved = (selectedEmployee.pageAccess || []).filter(
        (p) => !knownPaths.has(p)
      );
      const finalGrants = Array.from(new Set([...cleanedGrants, ...preserved]));

      await updateDoc(doc(db, 'Employees', selectedEmployee.id), {
        pageAccess: finalGrants
      });

      setEmployees((prev) =>
        prev.map((e) =>
          e.id === selectedEmployee.id ? { ...e, pageAccess: finalGrants } : e
        )
      );
      setDraftGrants(finalGrants);
      toast.success(`${selectedEmployee.eName} ${selectedEmployee.eSurname} için sayfa yetkileri kaydedildi`);
    } catch (error) {
      console.error('Error saving page access:', error);
      toast.error('Yetkiler kaydedilirken bir hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const openPasswordModal = () => {
    setNewPassword('');
    setSendEmail(true);
    setPwError('');
    setShowPasswordModal(true);
  };

  const handleSetPassword = async () => {
    if (!selectedEmployee) return;
    setPwError('');

    if (!selectedEmployee.eMail) {
      setPwError('Bu çalışanın e-posta adresi yok, şifre değiştirilemez.');
      return;
    }
    if (!newPassword || newPassword.length < 6) {
      setPwError('Şifre en az 6 karakter olmalıdır.');
      return;
    }

    setPwSaving(true);
    try {
      if (!auth.currentUser) {
        throw new Error('Oturum bulunamadı. Lütfen yeniden giriş yapın.');
      }
      const idToken = await auth.currentUser.getIdToken();

      const response = await fetch(SET_PASSWORD_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          email: selectedEmployee.eMail,
          newPassword,
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Şifre güncellenemedi.');
      }

      // İsteğe bağlı: yeni şifreyi e-posta ile gönder
      if (sendEmail) {
        try {
          const mailRes = await fetch(CREDENTIALS_EMAIL_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              to: selectedEmployee.eMail,
              password: newPassword,
              displayName: `${selectedEmployee.eName || ''} ${selectedEmployee.eSurname || ''}`.trim(),
              loginUrl: 'https://management.habcarecro.com',
              fromName: 'HabcareCRO Yönetim',
            }),
          });
          if (!mailRes.ok) throw new Error('mail non-OK');
          toast.success('Şifre güncellendi ve e-posta ile gönderildi.');
        } catch (mailErr) {
          console.error('Şifre e-postası gönderilemedi:', mailErr);
          toast.warn('Şifre güncellendi fakat e-posta gönderilemedi. Bilgiyi manuel iletiniz.');
        }
      } else {
        toast.success('Şifre başarıyla güncellendi.');
      }

      setShowPasswordModal(false);
    } catch (error) {
      console.error('Şifre güncelleme hatası:', error);
      setPwError(error.message || 'Şifre güncellenirken bir hata oluştu.');
    } finally {
      setPwSaving(false);
    }
  };

  const filteredEmployees = useMemo(() => {
    const term = employeeSearch.trim().toLowerCase();
    if (!term) return employees;
    return employees.filter((e) =>
      `${e.eName || ''} ${e.eSurname || ''}`.toLowerCase().includes(term) ||
      (e.eMail || '').toLowerCase().includes(term)
    );
  }, [employees, employeeSearch]);

  // Live accessible-page count for the selected employee (using the draft)
  const draftAccessibleCount = useMemo(() => {
    if (!selectedEmployee) return 0;
    return allPages.filter(
      (p) => pageState(p, draftGrants, selectedEmployee.eMail, selectedRoles) !== 'none'
    ).length;
  }, [allPages, draftGrants, selectedEmployee, selectedRoles]);

  return (
    <div className={`${styles.page} ${isDarkMode ? styles.darkMode : ''}`}>
      <header className={styles.header}>
        <div className={styles.headerTitle}>
          <FaUserLock className={styles.headerIcon} />
          <div>
            <h1>Sayfa Yetkileri</h1>
            <p className={styles.headerSubtitle}>
              Bir çalışan seçin, erişebileceği sayfaları görün ve dilediğiniz sayfayı açıp kapatın. Yaptığınız seçimler kaydedilir.
            </p>
          </div>
        </div>
        <div className={styles.adminButtonsGroup}>
          <button
            className={styles.adminsButton}
            onClick={() => setShowStatsModal(true)}
            style={{ background: '#0ea5e9' }}
          >
            <FaChartBar />
            Kullanım İstatistikleri
          </button>
          <button
            className={styles.adminsButton}
            onClick={() => setShowOnlineUsersModal(true)}
            style={{ background: '#22c55e' }}
          >
            <FaCircle />
            Çevrimiçi Olanlar
            <span className={styles.adminsButtonCount}>{onlineEmployees.length}</span>
          </button>
          <button
            className={styles.adminsButton}
            onClick={() => setShowRestrictedAdminsModal(true)}
          >
            <FaUserShield />
            Kısıtlı Adminler
            <span className={styles.adminsButtonCount}>{restrictedAdminEmployees.length}</span>
          </button>
          <button
            className={styles.adminsButton}
            onClick={() => setShowAdminsModal(true)}
          >
            <FaUserShield />
            Adminler
            <span className={styles.adminsButtonCount}>{adminEmployees.length}</span>
          </button>
        </div>
      </header>

      <div className={styles.layout}>
        {/* Employee list */}
        <aside className={styles.employeePanel}>
          <div className={styles.searchBox}>
            <FaSearch className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Çalışan ara..."
              value={employeeSearch}
              onChange={(e) => setEmployeeSearch(e.target.value)}
            />
            {employeeSearch && (
              <button className={styles.clearBtn} onClick={() => setEmployeeSearch('')}>
                <FaTimes />
              </button>
            )}
          </div>

          <div className={styles.employeeList}>
            {loading ? (
              <div className={styles.emptyHint}>Yükleniyor...</div>
            ) : filteredEmployees.length === 0 ? (
              <div className={styles.emptyHint}>Çalışan bulunamadı</div>
            ) : (
              filteredEmployees.map((emp) => {
                const isOnline = onlineUsers.has(emp.id);
                return (
                  <button
                    key={emp.id}
                    className={`${styles.employeeCard} ${selectedId === emp.id ? styles.employeeCardActive : ''}`}
                    onClick={() => selectEmployee(emp)}
                  >
                    <span className={styles.avatarContainer}>
                      <span className={styles.avatar}>{initials(emp)}</span>
                      <span 
                        className={`${styles.onlineIndicator} ${isOnline ? styles.onlineIndicatorActive : ''}`}
                        title={isOnline ? 'Online' : 'Offline'}
                      >
                        <FaCircle />
                      </span>
                    </span>
                    <span className={styles.employeeInfo}>
                      <span className={styles.employeeName}>
                        {emp.eName} {emp.eSurname}
                      </span>
                      <span className={styles.employeeEmail}>{emp.eMail || '—'}</span>
                      {/* Çevrimiçi çalışanlarda o an açık olan sayfa */}
                      {isOnline && onlineActivity[emp.id]?.page && (
                        <span className={styles.currentPageLine}>
                          <FaMapMarkerAlt className={styles.currentPageIcon} />
                          <span className={styles.currentPageTitle}>
                            {onlineActivity[emp.id].page.title}
                          </span>
                          {onlineActivity[emp.id].duration && (
                            <span className={styles.currentPageDuration}>
                              {onlineActivity[emp.id].duration}
                            </span>
                          )}
                        </span>
                      )}
                    </span>
                    <span className={styles.accessCount} title="Erişebildiği sayfa sayısı">
                      {accessibleCount(emp)}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </aside>

        {/* Page access editor */}
        <section className={styles.editorPanel}>
          {!selectedEmployee ? (
            <div className={styles.placeholder}>
              <FaUsers className={styles.placeholderIcon} />
              <h2>Bir çalışan seçin</h2>
              <p>Soldaki listeden bir çalışan seçtiğinizde, erişebildiği sayfaları burada düzenleyebilirsiniz.</p>
            </div>
          ) : (
            <>
              <div className={styles.editorHeader}>
                <span className={styles.avatarLarge}>{initials(selectedEmployee)}</span>
                <div className={styles.editorHeaderInfo}>
                  <h2>
                    {selectedEmployee.eName} {selectedEmployee.eSurname}
                  </h2>
                  <div className={styles.editorHeaderMeta}>
                    <span>{selectedEmployee.eMail || '—'}</span>
                  </div>
                  <div className={styles.roleChips}>
                    {selectedRoles.map((r) => (
                      <span key={r} className={styles.roleChip}>{r}</span>
                    ))}
                  </div>
                </div>
                <div className={styles.editorHeaderActions}>
                  <button
                    className={styles.passwordButton}
                    onClick={openPasswordModal}
                    title="Bu kullanıcının şifresini değiştir"
                  >
                    <FaKey /> Şifre Değiştir
                  </button>
                  <div className={styles.accessSummary}>
                    <span className={styles.accessSummaryValue}>{draftAccessibleCount}</span>
                    <span className={styles.accessSummaryLabel}>/ {allPages.length} sayfa</span>
                  </div>
                </div>
              </div>

              <div className={styles.legend}>
                <span className={styles.legendItem}><span className={`${styles.dot} ${styles.dotRole}`} /> Rol ile erişim</span>
                <span className={styles.legendItem}><span className={`${styles.dot} ${styles.dotSpecial}`} /> Özel erişim (kod)</span>
                <span className={styles.legendItem}><span className={`${styles.dot} ${styles.dotGranted}`} /> Eklendi</span>
                <span className={styles.legendItem}><span className={`${styles.dot} ${styles.dotNone}`} /> Erişim yok</span>
              </div>

              <div className={styles.pageSearchBox}>
                <FaSearch className={styles.searchIcon} />
                <input
                  type="text"
                  placeholder="Sayfa ara..."
                  value={pageSearch}
                  onChange={(e) => setPageSearch(e.target.value)}
                />
                {pageSearch && (
                  <button className={styles.clearBtn} onClick={() => setPageSearch('')}>
                    <FaTimes />
                  </button>
                )}
              </div>

              <div className={styles.pageGroups}>
                {groupedPages.map((section) => (
                  <div key={section.group} className={styles.pageGroup}>
                    <div className={styles.pageGroupTitle}>
                      <FaLayerGroup className={styles.pageGroupIcon} />
                      {section.group}
                    </div>
                    {section.pages.map((page) => {
                      const state = pageState(page, draftGrants, selectedEmployee.eMail, selectedRoles);
                      const locked = state === 'role' || state === 'special';
                      const on = state !== 'none';
                      return (
                        <div key={page.path} className={`${styles.pageRow} ${on ? styles.pageRowOn : ''}`}>
                          <div className={styles.pageRowInfo}>
                            <span className={styles.pageRowTitle}>{page.title}</span>
                            <span className={styles.pageRowPath}>{page.path}</span>
                          </div>

                          {state === 'role' && (
                            <span className={`${styles.statusTag} ${styles.statusRole}`}>
                              <FaLock /> Rol
                            </span>
                          )}
                          {state === 'special' && (
                            <span className={`${styles.statusTag} ${styles.statusSpecial}`}>
                              <FaLock /> Kod
                            </span>
                          )}
                          {state === 'granted' && (
                            <span className={`${styles.statusTag} ${styles.statusGranted}`}>
                              <FaCheck /> Eklendi
                            </span>
                          )}

                          <button
                            className={`${styles.toggle} ${on ? styles.toggleOn : ''} ${locked ? styles.toggleLocked : ''}`}
                            onClick={() => !locked && toggleGrant(page)}
                            disabled={locked}
                            title={
                              locked
                                ? 'Bu erişim rol veya kod tarafından sağlanıyor, buradan kaldırılamaz'
                                : on
                                ? 'Bu sayfayı kaldır'
                                : 'Bu sayfayı ekle'
                            }
                          >
                            <span className={styles.toggleKnob} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ))}
                {groupedPages.length === 0 && (
                  <div className={styles.emptyHint}>Arama ile eşleşen sayfa yok</div>
                )}
              </div>

              {hasChanges && (
                <div className={styles.saveBar}>
                  <span className={styles.saveBarText}>Kaydedilmemiş değişiklikler var</span>
                  <button className={styles.resetButton} onClick={resetDraft} disabled={saving}>
                    <FaUndo /> Sıfırla
                  </button>
                  <button className={styles.saveButton} onClick={handleSave} disabled={saving}>
                    <FaSave /> <span>{saving ? 'Kaydediliyor...' : 'Kaydet'}</span>
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      </div>

      {showPasswordModal && selectedEmployee && (
        <div
          className={styles.modalOverlay}
          onClick={() => !pwSaving && setShowPasswordModal(false)}
        >
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>
                <FaKey className={styles.modalTitleIcon} />
                Şifre Değiştir
              </h2>
              <button
                className={styles.modalClose}
                onClick={() => !pwSaving && setShowPasswordModal(false)}
              >
                <FaTimes />
              </button>
            </div>

            <div className={styles.modalBody}>
              <p className={styles.pwTargetInfo}>
                <strong>{selectedEmployee.eName} {selectedEmployee.eSurname}</strong>
                <br />
                <span className={styles.employeeEmail}>{selectedEmployee.eMail || '—'}</span>
              </p>

              <label className={styles.pwLabel}>Yeni Şifre</label>
              <div className={styles.pwInputRow}>
                <input
                  type="text"
                  className={styles.pwInput}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="En az 6 karakter"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className={styles.pwGenerateBtn}
                  onClick={() => setNewPassword(generateStrongPassword())}
                >
                  Oluştur
                </button>
              </div>

              <label className={styles.pwCheckboxRow}>
                <input
                  type="checkbox"
                  checked={sendEmail}
                  onChange={(e) => setSendEmail(e.target.checked)}
                />
                <FaEnvelope className={styles.pwCheckboxIcon} />
                Yeni şifreyi kullanıcıya e-posta ile gönder
              </label>

              {pwError && <p className={styles.pwError}>{pwError}</p>}
            </div>

            <div className={styles.pwModalFooter}>
              <button
                className={styles.resetButton}
                onClick={() => setShowPasswordModal(false)}
                disabled={pwSaving}
              >
                İptal
              </button>
              <button
                className={styles.saveButton}
                onClick={handleSetPassword}
                disabled={pwSaving}
              >
                {pwSaving ? <FaSpinner className={styles.spinner} /> : <FaKey />}
                <span>{pwSaving ? 'Kaydediliyor...' : 'Şifreyi Güncelle'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {showAdminsModal && (
        <div className={styles.modalOverlay} onClick={() => setShowAdminsModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>
                <FaUserShield className={styles.modalTitleIcon} />
                Admin Yetkili Çalışanlar ({adminEmployees.length})
              </h2>
              <button
                className={styles.modalClose}
                onClick={() => setShowAdminsModal(false)}
              >
                <FaTimes />
              </button>
            </div>

            <div className={styles.modalBody}>
              {loading ? (
                <div className={styles.emptyHint}>Yükleniyor...</div>
              ) : adminEmployees.length === 0 ? (
                <div className={styles.emptyHint}>
                  Yetkilendirmesi "admin" olan çalışan bulunmuyor.
                </div>
              ) : (
                <div className={styles.adminList}>
                  {adminEmployees.map((emp) => (
                    <button
                      key={emp.id}
                      className={styles.adminCard}
                      onClick={() => {
                        selectEmployee(emp);
                        setShowAdminsModal(false);
                      }}
                      title="Bu çalışanın sayfa yetkilerini düzenle"
                    >
                      <span className={styles.avatar}>{initials(emp)}</span>
                      <span className={styles.employeeInfo}>
                        <span className={styles.employeeName}>
                          {emp.eName} {emp.eSurname}
                        </span>
                        <span className={styles.employeeEmail}>{emp.eMail || '—'}</span>
                      </span>
                      <span className={styles.roleChip}>admin</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showRestrictedAdminsModal && (
        <div className={styles.modalOverlay} onClick={() => setShowRestrictedAdminsModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>
                <FaUserShield className={styles.modalTitleIcon} />
                Kısıtlı Admin Yetkili Çalışanlar ({restrictedAdminEmployees.length})
              </h2>
              <button
                className={styles.modalClose}
                onClick={() => setShowRestrictedAdminsModal(false)}
              >
                <FaTimes />
              </button>
            </div>

            <div className={styles.modalBody}>
              {loading ? (
                <div className={styles.emptyHint}>Yükleniyor...</div>
              ) : restrictedAdminEmployees.length === 0 ? (
                <div className={styles.emptyHint}>
                  Yetkilendirmesi "Kısıtlı Admin" olan çalışan bulunmuyor.
                </div>
              ) : (
                <div className={styles.adminList}>
                  {restrictedAdminEmployees.map((emp) => (
                    <button
                      key={emp.id}
                      className={styles.adminCard}
                      onClick={() => {
                        selectEmployee(emp);
                        setShowRestrictedAdminsModal(false);
                      }}
                      title="Bu çalışanın sayfa yetkilerini düzenle"
                    >
                      <span className={styles.avatar}>{initials(emp)}</span>
                      <span className={styles.employeeInfo}>
                        <span className={styles.employeeName}>
                          {emp.eName} {emp.eSurname}
                        </span>
                        <span className={styles.employeeEmail}>{emp.eMail || '—'}</span>
                      </span>
                      <span className={styles.roleChip}>Kısıtlı Admin</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showOnlineUsersModal && (
        <div className={styles.modalOverlay} onClick={() => setShowOnlineUsersModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>
                <FaCircle className={styles.modalTitleIcon} style={{ color: '#22c55e' }} />
                Çevrimiçi Kullanıcılar ({onlineEmployees.length})
              </h2>
              <button
                className={styles.modalClose}
                onClick={() => setShowOnlineUsersModal(false)}
              >
                <FaTimes />
              </button>
            </div>

            <div className={styles.modalBody}>
              {loading ? (
                <div className={styles.emptyHint}>Yükleniyor...</div>
              ) : onlineEmployees.length === 0 ? (
                <div className={styles.emptyHint}>
                  Şu anda çevrimiçi kullanıcı bulunmuyor.
                </div>
              ) : (
                <div className={styles.adminList}>
                  {onlineEmployees.map((emp) => {
                    const roles = getRoles(emp);
                    const roleLabel = roles.includes('admin') ? 'admin' : 
                                     roles.includes('Kısıtlı Admin') ? 'Kısıtlı Admin' : 
                                     'Çalışan';
                    const activity = onlineActivity[emp.id] || {};
                    return (
                      <div key={emp.id} className={styles.onlineCard}>
                        <span className={styles.avatarContainer}>
                          <span className={styles.avatar}>{initials(emp)}</span>
                          <span className={`${styles.onlineIndicator} ${styles.onlineIndicatorActive}`}>
                            <FaCircle />
                          </span>
                        </span>
                        <span className={styles.employeeInfo}>
                          <span className={styles.employeeName}>
                            {emp.eName} {emp.eSurname}
                          </span>
                          <span className={styles.employeeEmail}>{emp.eMail || '—'}</span>
                          {/* Kullanıcının o an açık olan sayfası ve orada geçirdiği süre */}
                          <span className={styles.currentPageLine}>
                            <FaMapMarkerAlt className={styles.currentPageIcon} />
                            {activity.page ? (
                              <>
                                <span className={styles.currentPageGroup}>
                                  {activity.page.group}
                                </span>
                                <span className={styles.currentPageTitle}>
                                  {activity.page.title}
                                </span>
                                {activity.duration && (
                                  <span className={styles.currentPageDuration}>
                                    {activity.duration}
                                  </span>
                                )}
                              </>
                            ) : (
                              <span className={styles.currentPageUnknown}>
                                Sayfa bilgisi henüz gelmedi
                              </span>
                            )}
                          </span>
                        </span>
                        <span className={styles.onlineCardActions}>
                          <span className={styles.roleChip}>{roleLabel}</span>
                          <span className={styles.onlineCardButtons}>
                            <button
                              type="button"
                              className={styles.messageButton}
                              onClick={() => {
                                setMessageTarget(emp);
                                setMessageText('');
                              }}
                              title="Bu çalışana mesaj gönder"
                            >
                              <FaPaperPlane /> Mesaj
                            </button>
                            <button
                              type="button"
                              className={styles.accessButton}
                              onClick={() => {
                                selectEmployee(emp);
                                setShowOnlineUsersModal(false);
                              }}
                              title="Bu çalışanın sayfa yetkilerini düzenle"
                            >
                              <FaUserEdit /> Yetkiler
                            </button>
                          </span>
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {messageTarget && (
        <div
          className={styles.modalOverlay}
          onClick={() => !sendingMessage && setMessageTarget(null)}
        >
          <div
            className={`${styles.modal} ${styles.messageModal}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>
                <FaPaperPlane className={styles.modalTitleIcon} />
                Mesaj Gönder
              </h2>
              <button
                className={styles.modalClose}
                onClick={() => setMessageTarget(null)}
                disabled={sendingMessage}
              >
                <FaTimes />
              </button>
            </div>

            <div className={styles.modalBody}>
              <div className={styles.messageRecipient}>
                <span className={styles.avatar}>{initials(messageTarget)}</span>
                <span className={styles.employeeInfo}>
                  <span className={styles.employeeName}>
                    {messageTarget.eName} {messageTarget.eSurname}
                  </span>
                  <span className={styles.employeeEmail}>
                    {messageTarget.eMail || 'E-posta tanımlı değil'}
                  </span>
                </span>
              </div>

              <textarea
                className={styles.messageInput}
                rows={5}
                autoFocus
                placeholder="Mesajınızı yazın..."
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) sendMessage();
                }}
              />
              <p className={styles.messageHint}>
                Mesaj, çalışanın mesaj kutusuna düşer ve oradan cevaplayabilir.
                Göndermek için Ctrl + Enter.
              </p>
            </div>

            <div className={styles.messageActions}>
              <button
                type="button"
                className={styles.secondaryAction}
                onClick={() => setMessageTarget(null)}
                disabled={sendingMessage}
              >
                Vazgeç
              </button>
              <button
                type="button"
                className={styles.primaryAction}
                onClick={sendMessage}
                disabled={sendingMessage || !messageText.trim()}
              >
                {sendingMessage ? <FaSpinner className={styles.spin} /> : <FaPaperPlane />}
                {sendingMessage ? 'Gönderiliyor...' : 'Gönder'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showStatsModal && (
        <UsageStatsModal
          employees={employees}
          onlineUsers={onlineUsers}
          isDarkMode={isDarkMode}
          onClose={() => setShowStatsModal(false)}
        />
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        theme={isDarkMode ? 'dark' : 'light'}
      />
    </div>
  );
};

export default PageAccessManagementPage;
